import { map } from 'lodash-es'
import { onlyHeadMetadataRequired } from '#app/lib/metadata/update'
import { treq } from '#app/lib/treq'
import { objectEntries, objectValues } from '#app/lib/utils'
import { aggregateWorksRelationsClaims, inverseLabels } from '#entities/components/lib/claims_helpers'
import { byPublicationDate, getReverseClaims, getEntities, getEntitiesAttributesByUris, type SerializedEntity, serializeEntities } from '#entities/lib/entities'
import { entityDataShouldBeRefreshed } from '#entities/lib/entity_refresh'
import { fetchRelatedEntities } from '#entities/lib/fetch_related_entities'
import { addEntitiesImages } from '#entities/lib/types/work_alt'
import type { SortFunction } from '#server/types/common'
import type { EntityUri, ExtendedEntityType, PropertyUri, SimplifiedClaims } from '#server/types/entity'
import { getCurrentLang, i18n, I18n } from '#user/lib/i18n'

const subEntitiesProp = {
  work: 'wdt:P629',
  serie: 'wdt:P179',
} as const

export interface SubEntitiesBrowserSectionData {
  key: string
  uris: EntityUri[]
  entities?: SerializedEntity[]
  label?: string
  subEntityType?: ExtendedEntityType
  parentUri?: EntityUri
  parentEntity?: SerializedEntity
  subEntityRelationProperty?: PropertyUri
  searchable?: boolean
  isCompactDisplay?: boolean
  sortingType?: 'article' | 'work' | 'seriePart' | null
  context?: string
}

type SectionsGetter = ({ entity, refresh, property }: { entity: SerializedEntity, refresh: boolean, property?: PropertyUri }) => Promise<SubEntitiesBrowserSectionData[]>

const urisGetterByType: Partial<Record<ExtendedEntityType | 'claim', SectionsGetter>> = {
  serie: async ({ entity, refresh }) => {
    const { uri: parentUri } = entity

    const { parts } = await treq('entities', 'get', 'serie-parts', { uri: parentUri, refresh })
    return [
      {
        key: 'serieParts',
        subEntityType: 'work',
        parentUri,
        parentEntity: entity,
        uris: map(parts, 'uri'),
        subEntityRelationProperty: 'wdt:P179',
        sortingType: 'seriePart',
      },
    ]
  },
  human: async ({ entity, refresh }) => {
    const { uri: parentUri } = entity
    const subEntityRelationProperty = 'wdt:P50'

    const { series, works, articles } = await await treq('entities', 'get', 'author-works', { uri: parentUri, refresh })
    return [
      {
        key: 'series',
        label: I18n('series'),
        subEntityType: 'serie',
        parentUri,
        parentEntity: entity,
        subEntityRelationProperty,
        uris: map(series, 'uri'),
      },
      {
        key: 'works',
        label: I18n('works'),
        subEntityType: 'work',
        parentUri,
        parentEntity: entity,
        subEntityRelationProperty,
        uris: map(works, 'uri'),
      },
      {
        key: 'articles',
        label: I18n('articles'),
        uris: map(articles, 'uri'),
        searchable: false,
        isCompactDisplay: true,
        sortingType: 'article',
      },
    ]
  },
  publisher: async ({ entity, refresh }) => {
    const { uri: parentUri } = entity
    const subEntityRelationProperty = 'wdt:P123'

    const { collections, editions } = await await treq('entities', 'get', 'publisher-publications', { uri: parentUri, refresh })
    return [
      {
        key: 'collections',
        label: I18n('collections'),
        subEntityType: 'collection',
        parentUri,
        parentEntity: entity,
        subEntityRelationProperty,
        uris: map(collections, 'uri'),
      },
      {
        key: 'editions',
        label: I18n('editions'),
        subEntityType: 'edition',
        parentUri,
        parentEntity: entity,
        subEntityRelationProperty,
        uris: map(editions, 'uri'),
        searchable: false,
      },
    ]
  },
  collection: async ({ entity, refresh }) => {
    const { uri: parentUri } = entity
    const subEntityRelationProperty = 'wdt:P195'

    const uris = await getReverseClaims(subEntityRelationProperty, parentUri, refresh)
    return [
      {
        key: 'editions',
        label: I18n('editions'),
        subEntityType: 'edition',
        parentUri,
        parentEntity: entity,
        subEntityRelationProperty,
        uris,
        searchable: false,
      },
    ]
  },
  article: async ({ entity }) => {
    const { claims } = entity
    const uris = claims['wdt:P2860']
    return [
      {
        key: 'articles',
        label: I18n('cites articles'),
        uris,
        searchable: false,
        isCompactDisplay: true,
        sortingType: 'article',
      },
    ]
  },
  claim: async ({ entity, property, refresh }) => {
    const { uri, label: entityLabel } = entity
    const uris = await getReverseClaims(property, uri, refresh)
    const propLabel = inverseLabels[property] || ''
    const label = i18n(propLabel, { name: entityLabel })
    let sortingType: 'work' | null
    if (property === 'wdt:P69') sortingType = null
    else sortingType = 'work'
    return [
      {
        key: 'subjects',
        label,
        uris,
        sortingType,
      },
    ]
  },
} as const

interface GetSubEntitiesSectionsParams {
  entity: SerializedEntity
  sortFn?: SortFunction<SerializedEntity>
  property?: PropertyUri
}

export async function getSubEntitiesSections ({ entity, sortFn = byPublicationDate, property }: GetSubEntitiesSectionsParams) {
  if (onlyHeadMetadataRequired) return
  const { uri, type } = entity
  const refresh = entityDataShouldBeRefreshed(uri)
  let sections: SubEntitiesBrowserSectionData[]
  if (property) {
    const getSubEntitiesUris = urisGetterByType.claim
    sections = await getSubEntitiesUris({ entity, property, refresh })
  } else {
    const getSubEntitiesUris = urisGetterByType[type]
    sections = await getSubEntitiesUris({ entity, refresh })
  }
  await Promise.all(sections.map(fetchSectionEntitiesFactory({ sortFn, parentEntityType: type })))
  return sections
}

// Limiting the amount of entities to not overload the server
// ie. `entity/wdt:P1433-wd:Q180445`
// Ideas to increase limit:
//   - batch requests and retry on failure and handle facets entities queries the same way
//   - or build facets on the server (like with inventory-view for the inventory browser), and only display the first entities of the facet-filtered list, fetching more on scroll.
const entitiesLimit = 200
const limitedTypes = new Set([ 'publisher', 'genre', 'subject', 'article' ])

function truncateTooManyUris (section, parentEntityType) {
  const { uris = [] } = section
  const urisCount = uris.length
  if (urisCount < entitiesLimit) return
  if (limitedTypes.has(parentEntityType)) {
    section.uris = uris.splice(0, entitiesLimit)
    section.context = i18n('Too many entities requested (%{entitiesCount}). Only %{limit} are displayed.', {
      entitiesCount: urisCount,
      limit: entitiesLimit,
    })
  }
}

interface FetchSectionEntitiesFactoryParams {
  sortFn?: GetSubEntitiesSectionsParams['sortFn']
  parentEntityType: ExtendedEntityType
}

export function fetchSectionEntitiesFactory ({ sortFn, parentEntityType }: FetchSectionEntitiesFactoryParams) {
  return async function fetchSectionEntities (section: SubEntitiesBrowserSectionData) {
    truncateTooManyUris(section, parentEntityType)
    const uris = section?.uris || []
    const { entities } = await getEntitiesAttributesByUris({
      uris,
      attributes: [
        'info',
        'labels',
        'descriptions',
        'claims',
        'image',
      ],
      lang: getCurrentLang(),
      // Do not set aliases, as it would add duplicated entities
      setRedirectionAliases: false,
    })

    // This prevents displaying several entities with the same canonical uri
    // as might happen if both inv and wd queries returned conflicting entities
    // TODO: remove once all inv and wd entities sharing the same ISBN have been merged
    if (uris.some(uri => uri.startsWith('isbn'))) {
      for (const [ uri, entity ] of objectEntries(entities)) {
        if (entity.uri !== uri && entities[uri] != null) delete entities[entity.uri]
      }
    }

    section.entities = (await serializeEntities(objectValues(entities))).sort(sortFn)
    await addEntitiesImages(section.entities)
    await fetchRelatedEntities(section.entities, parentEntityType)
    return section
  }
}

export async function getSubEntities (type: keyof typeof subEntitiesProp, uri: EntityUri) {
  const refresh = entityDataShouldBeRefreshed(uri)
  const uris = await getReverseClaims(subEntitiesProp[type], uri, refresh)
  return getEntities(uris)
}

export function addWorksClaims (claims: SimplifiedClaims, works: SerializedEntity[]) {
  const worksClaims = aggregateWorksRelationsClaims(works)
  return Object.assign(claims, worksClaims)
}
